import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ArrowLeft, Send, Search, Loader2, MessageSquare, CheckCircle } from 'lucide-react';
import { LogoIcon } from './Logo';
import { toast } from 'sonner';

interface SupportPageProps {
  onBack?: () => void;
}

// API client functions for support (no authentication required)
const API_BASE_URL = 'https://btgtr-copilot-backend.better-to-give-than-receive.workers.dev/api/v1';

async function submitSupportRequest(contact: string, message: string) {
  const response = await fetch(`${API_BASE_URL}/support/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ contact, message }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to submit support request');
  }

  return response.json();
}

async function checkSupportStatus(contact: string) {
  const response = await fetch(`${API_BASE_URL}/support/status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ contact }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check support status');
  }

  return response.json();
}

export function SupportPage({ onBack }: SupportPageProps) {
  const [activeTab, setActiveTab] = useState<'submit' | 'status'>('submit');
  
  // Submit form state
  const [submitContact, setSubmitContact] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Status form state
  const [statusContact, setStatusContact] = useState('');
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [statusResults, setStatusResults] = useState<any[]>([]);
  const [statusChecked, setStatusChecked] = useState(false);

  // Get the current domain to construct the support email
  const getCurrentDomain = () => {
    if (typeof window === 'undefined') return 'bettertogivethanreceive.com';
    
    const hostname = window.location.hostname;
    
    // Remove 'www.' prefix if present
    const domain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    
    // For localhost or pages.dev preview domains, use default
    if (domain.includes('localhost') || domain.includes('.pages.dev')) {
      return 'bettertogivethanreceive.com';
    }
    
    return domain;
  };

  const supportEmail = `feedback@${getCurrentDomain()}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!submitContact.trim() || !submitMessage.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const result = await submitSupportRequest(submitContact.trim(), submitMessage.trim());
      
      console.log('[Support] Request submitted:', result);
      
      toast.success('Support request submitted!', {
        description: 'We will respond to you soon at the contact information provided.',
      });
      
      setSubmitSuccess(true);
      setSubmitContact('');
      setSubmitMessage('');
    } catch (error) {
      console.error('[Support] Error submitting request:', error);
      toast.error('Failed to submit support request', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!statusContact.trim()) {
      toast.error('Please enter your email or phone number');
      return;
    }

    setIsCheckingStatus(true);
    setStatusResults([]);
    setStatusChecked(false);

    try {
      const result = await checkSupportStatus(statusContact.trim());
      
      console.log('[Support] Status check result:', result);
      
      setStatusResults(result.requests || []);
      setStatusChecked(true);
      
      if (result.requests && result.requests.length > 0) {
        toast.success('Status update sent!', {
          description: `Found ${result.requests.length} support request${result.requests.length !== 1 ? 's' : ''}. Check your ${statusContact.includes('@') ? 'email' : 'phone'} for details.`,
        });
      } else {
        toast.info('No support requests found', {
          description: 'We did not find any support requests for this contact information.',
        });
      }
    } catch (error) {
      console.error('[Support] Error checking status:', error);
      toast.error('Failed to check support status', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsCheckingStatus(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {onBack && (
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        <Card className="border-2 border-emerald-100 shadow-xl">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="flex justify-center mb-2">
              <LogoIcon size="lg" />
            </div>
            <CardTitle className="text-3xl">Support Center</CardTitle>
            <CardDescription className="text-base">
              Get help with Better to Give than Receive
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Tab Navigation */}
            <div className="flex gap-2 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('submit')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  activeTab === 'submit'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-primary'
                }`}
              >
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Submit Request
              </button>
              <button
                onClick={() => setActiveTab('status')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  activeTab === 'status'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-primary'
                }`}
              >
                <Search className="w-4 h-4 inline mr-2" />
                Check Status
              </button>
            </div>

            {/* Submit Request Tab */}
            {activeTab === 'submit' && (
              <div className="space-y-6">
                {submitSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-green-900">Request Submitted Successfully!</h4>
                      <p className="text-sm text-green-700 mt-1">
                        We've received your support request and will respond soon at the contact information you provided.
                      </p>
                    </div>
                  </div>
                )}

                <div className="prose prose-sm max-w-none">
                  <p className="text-muted-foreground">
                    Need help with our gift-giving platform? Submit a support request below and our team will get back to you as soon as possible.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="submit-contact" className="block text-sm font-medium text-gray-700 mb-2">
                      Email or Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="submit-contact"
                      type="text"
                      placeholder="your.email@example.com or +1234567890"
                      value={submitContact}
                      onChange={(e) => setSubmitContact(e.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      We'll use this to respond to your request
                    </p>
                  </div>

                  <div>
                    <label htmlFor="submit-message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                      id="submit-message"
                      placeholder="Describe your issue or question..."
                      value={submitMessage}
                      onChange={(e) => setSubmitMessage(e.target.value)}
                      className="min-h-[150px] resize-none"
                      maxLength={5000}
                      disabled={isSubmitting}
                      required
                    />
                    <div className="text-xs text-muted-foreground mt-2 text-right">
                      {submitMessage.length}/5000 characters
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || !submitContact.trim() || !submitMessage.trim()}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Support Request
                      </>
                    )}
                  </Button>
                </form>
              </div>
            )}

            {/* Check Status Tab */}
            {activeTab === 'status' && (
              <div className="space-y-6">
                <div className="prose prose-sm max-w-none">
                  <p className="text-muted-foreground">
                    Check the status of your previously submitted support requests. Enter the email or phone number you used when submitting your request.
                  </p>
                </div>

                <form onSubmit={handleCheckStatus} className="space-y-4">
                  <div>
                    <label htmlFor="status-contact" className="block text-sm font-medium text-gray-700 mb-2">
                      Email or Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="status-contact"
                      type="text"
                      placeholder="your.email@example.com or +1234567890"
                      value={statusContact}
                      onChange={(e) => setStatusContact(e.target.value)}
                      disabled={isCheckingStatus}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter the same contact info you used when submitting your request
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={isCheckingStatus || !statusContact.trim()}
                    className="w-full bg-primary hover:bg-primary/90"
                    size="lg"
                  >
                    {isCheckingStatus ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Check Status
                      </>
                    )}
                  </Button>
                </form>

                {/* Status Results */}
                {statusChecked && (
                  <div className="mt-6 space-y-4">
                    {statusResults.length > 0 ? (
                      <>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-2">
                            Found {statusResults.length} Support Request{statusResults.length !== 1 ? 's' : ''}
                          </h4>
                          <p className="text-sm text-blue-700">
                            We've sent a detailed status update to your {statusContact.includes('@') ? 'email' : 'phone'}. 
                            Check your messages for more information.
                          </p>
                        </div>

                        <div className="space-y-3">
                          {statusResults.map((request) => (
                            <div
                              key={request.id}
                              className="bg-white border border-gray-200 rounded-lg p-4 space-y-2"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-mono text-muted-foreground">
                                  {request.id}
                                </span>
                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                  request.status === 'resolved'
                                    ? 'bg-green-100 text-green-700'
                                    : request.status === 'in_progress'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {request.status === 'resolved' ? 'Resolved' : 
                                   request.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">
                                {request.message.substring(0, 150)}
                                {request.message.length > 150 ? '...' : ''}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Submitted: {new Date(request.submittedAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                        <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-2">No Support Requests Found</h4>
                        <p className="text-sm text-gray-600">
                          We didn't find any support requests for this contact information.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Additional Help */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Resources</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  • Response time: We typically respond within 1-2 business days
                </p>
                <p>
                  • Business hours: Monday-Friday, 9 AM - 5 PM EST
                </p>
                <p>
                  • Email: <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">{supportEmail}</a>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
